import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ComplianceBadge } from "@/components/ComplianceBadge";
import { ShieldPlus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const CERT_TYPES = [
  { v: "CMT", l: "CMT — Medication Technician" },
  { v: "CPR", l: "CPR" },
  { v: "FIRST_AID", l: "First Aid" },
  { v: "RN_DELEGATION", l: "RN Delegation" },
];

export default function Certifications() {
  const { user } = useAuth();
  const [certs, setCerts] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ user_id:"", type:"CMT", issued_at:"", expires_at:"", issuer:"" });

  const load = async () => {
    const c = await api.get("/certifications");
    setCerts(c.data);
    if (user.role !== "dsp") {
      const u = await api.get("/users", { params: { limit: 500 } });
      setUsers(u.data.data);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/certifications", {
        ...f,
        issued_at: new Date(f.issued_at).toISOString(),
        expires_at: new Date(f.expires_at).toISOString(),
      });
      toast.success("Credential saved");
      setOpen(false); setF({ user_id:"", type:"CMT", issued_at:"", expires_at:"", issuer:"" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this credential?")) return;
    try { await api.delete(`/certifications/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const userName = (uid) => users.find((u)=>u.id===uid)?.name || uid;

  const canManage = user.role === "admin" || user.role === "rn";

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="accp-label">Training & Compliance</div>
          <h1 className="text-4xl font-semibold text-primary" style={{fontFamily:'Outfit'}}>
            {user.role === "dsp" ? "My Credentials" : "Certifications"}
          </h1>
          <p className="text-muted-foreground mt-1">CMT, CPR, RN Delegation — tracked with status indicators.</p>
        </div>
        {canManage && (
          <button className="accp-btn-primary" onClick={()=>setOpen(true)} data-testid="add-cert-btn">
            <ShieldPlus size={16}/> Add credential
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {certs.map((c) => (
          <div key={c.id} className="accp-card p-5" data-testid={`cert-${c.id}`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="accp-label">{c.type.replace("_"," ")}</div>
                <div className="font-semibold text-primary mt-0.5" style={{fontFamily:'Outfit'}}>
                  {user.role === "dsp" ? "You" : userName(c.user_id)}
                </div>
              </div>
              <ComplianceBadge status={c.status}/>
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <div>Issued: <span className="text-primary">{c.issued_at?.slice(0,10)}</span></div>
              <div>Expires: <span className="text-primary">{c.expires_at?.slice(0,10)}</span></div>
              {c.issuer && <div>Issuer: <span className="text-primary">{c.issuer}</span></div>}
              {c.expiring_soon && (
                <div className="mt-2 text-xs font-semibold" style={{color:"#B15C00"}}>Expiring within 30 days</div>
              )}
            </div>
            {canManage && (
              <button className="accp-btn-ghost mt-3 text-xs text-destructive" onClick={()=>del(c.id)}>
                <Trash2 size={13}/> Delete
              </button>
            )}
          </div>
        ))}
        {certs.length===0 && <div className="text-muted-foreground col-span-full">No credentials found.</div>}
      </div>

      {open && (
        <div className="accp-modal-shell" onClick={()=>setOpen(false)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={submit} className="accp-modal-card space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-primary" style={{fontFamily:'Outfit'}}>Record credential</h3>
              <button type="button" onClick={()=>setOpen(false)}><X size={18}/></button>
            </div>
            <label className="block"><span className="accp-label">Staff</span>
              <select className="accp-input mt-1" required value={f.user_id} onChange={(e)=>setF({...f, user_id:e.target.value})}>
                <option value="">Select…</option>
                {users.filter((u)=>u.role!=="admin").map((u)=><option key={u.id} value={u.id}>{u.name} — {u.role}</option>)}
              </select>
            </label>
            <label className="block"><span className="accp-label">Type</span>
              <select className="accp-input mt-1" value={f.type} onChange={(e)=>setF({...f, type:e.target.value})}>
                {CERT_TYPES.map((t)=><option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label><span className="accp-label">Issued</span>
                <input type="date" required className="accp-input mt-1" value={f.issued_at} onChange={(e)=>setF({...f, issued_at:e.target.value})}/>
              </label>
              <label><span className="accp-label">Expires</span>
                <input type="date" required className="accp-input mt-1" value={f.expires_at} onChange={(e)=>setF({...f, expires_at:e.target.value})}/>
              </label>
            </div>
            <label className="block"><span className="accp-label">Issuer</span>
              <input className="accp-input mt-1" value={f.issuer} onChange={(e)=>setF({...f, issuer:e.target.value})}/>
            </label>
            <button type="submit" className="accp-btn-primary w-full" data-testid="cert-submit">Save credential</button>
          </form>
        </div>
      )}
    </div>
  );
}
